/**
 * FilterSection Component
 * 라이더 현황 검색 필터 영역
 */
import React, { useMemo } from 'react';
import { Supplier, CONTRACT_STATUS_OPTIONS } from '../types/riderStatus.types';
import SearchableSelect, { SelectOption } from '@/shared/components/forms/SearchableSelect';
import '@/shared/styles/search-form.css';

type FilterState = {
    centerId: string;
    riderName: string;
    riderId: string;
    phoneNum: string;
    contStatus: string;
}

type FilterSectionProps = {
    filters: FilterState;
    suppliers: Supplier[];
    suppliersLoading: boolean;
    userType: string | null;
    loading: boolean;
    excelDownloading: boolean;
    onFilterChange: (key: string, value: string) => void;
    onKeyDown: (e: React.KeyboardEvent) => void;
    onSearch: () => void;
    onExcelDownload: () => void;
}

const FilterSection: React.FC<FilterSectionProps> = ({
    filters,
    suppliers,
    suppliersLoading,
    userType,
    loading,
    excelDownloading,
    onFilterChange,
    onKeyDown,
    onSearch,
    onExcelDownload
}) => {
    const isHeadquarters = userType === 'HEADQUARTERS';
    const isSupplier = userType === 'SUPPLIER';

    // 협력사 옵션을 SelectOption 형식으로 변환
    const supplierOptions: SelectOption[] = useMemo(() => {
        return suppliers.map((supplier, index) => ({
            value: supplier.centerId ?? `${supplier.name}-${index}`,
            label: supplier.name
        }));
    }, [suppliers]);

    return (
        <div className="card search-card">
            <div className="card-body">
                <div className={`search-form ${isSupplier ? 'supplier-view' : ''}`}>
                    {/* 본사 사용자만 협력사 선택 필드 표시 */}
                    {isHeadquarters && (
                        <div className="form-group">
                            <div className="form-field">
                                <label className="form-label">
                                    <i className="fas fa-building" />
                                    협력사
                                </label>
                                <SearchableSelect
                                    options={supplierOptions}
                                    value={filters.centerId}
                                    onChange={(value) => onFilterChange('centerId', value)}
                                    placeholder={suppliersLoading ? '로딩 중...' : '협력사 검색...'}
                                    disabled={suppliersLoading}
                                    showAllOption={true}
                                    allOptionLabel="전체"
                                />
                            </div>
                        </div>
                    )}

                    {/* 이름 */}
                    <div className="form-group">
                        <div className="form-field">
                            <label className="form-label">
                                <i className="fas fa-user" />
                                이름
                            </label>
                            <input
                                type="text"
                                className="text-input"
                                value={filters.riderName}
                                onChange={(e) => onFilterChange('riderName', e.target.value)}
                                onKeyDown={onKeyDown}
                                placeholder="라이더 이름..."
                            />
                        </div>
                    </div>

                    {/* 아이디 */}
                    <div className="form-group">
                        <div className="form-field">
                            <label className="form-label">
                                <i className="fas fa-id-card" />
                                아이디
                            </label>
                            <input
                                type="text"
                                className="text-input"
                                value={filters.riderId}
                                onChange={(e) => onFilterChange('riderId', e.target.value)}
                                onKeyDown={onKeyDown}
                                placeholder="라이더 아이디..."
                            />
                        </div>
                    </div>

                    {/* 계약 상태 */}
                    <div className="form-group">
                        <div className="form-field">
                            <label className="form-label">
                                <i className="fas fa-file-contract" />
                                계약 상태
                            </label>
                            <div className="status-select-wrapper">
                                <select
                                    className="status-select"
                                    value={filters.contStatus}
                                    onChange={(e) => onFilterChange('contStatus', e.target.value)}
                                >
                                    {CONTRACT_STATUS_OPTIONS.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* 버튼 그룹 */}
                    <div className="form-group button-group">
                        <button
                            className="btn btn-success excel-btn"
                            onClick={onExcelDownload}
                            disabled={loading || excelDownloading}
                        >
                            {excelDownloading ? (
                                <>
                                    <i className="fas fa-spinner fa-spin" />
                                    다운로드 중...
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-file-excel" />
                                    엑셀
                                </>
                            )}
                        </button>
                        <button
                            className="btn btn-primary"
                            onClick={onSearch}
                            disabled={loading}
                        >
                            <i className="fas fa-search" />
                            검색
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FilterSection;
